import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mic2, Newspaper, Gift, Megaphone, CalendarDays, Check, Mail, User, ArrowRight } from "lucide-react";
import { api } from "../api";

const BENEFITS = [
  { icon: Mic2, label: "Exclusive Interviews", body: "Sit-downs with the artists behind every drop." },
  { icon: Newspaper, label: "Breaking News", body: "Hip-hop news in your inbox, first." },
  { icon: Gift, label: "Monthly Giveaways", body: "Merch, mixtapes, and Culture Pass for members." },
  { icon: Megaphone, label: "Artist Opportunities", body: "Open calls, A&R hunts, and paid gigs." },
  { icon: CalendarDays, label: "Event Announcements", body: "Shows, festivals, listening parties — early." },
];

export default function CultureClubPage() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { is_new, subscriber_count }
  const [err, setErr] = useState("");
  const [subscriberCount, setSubscriberCount] = useState(null);

  useEffect(() => {
    // Lightweight public counter (admin-only endpoint, so this just stays null for non-admins)
    api.get("/subscribe/stats").then((r) => setSubscriberCount(r.data?.subscriber_count)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await api.post("/subscribe", { name: form.name, email: form.email, source: "culture-club" });
      setResult(r.data);
      setSubscriberCount(r.data.subscriber_count);
    } catch (e2) {
      setErr(e2.response?.data?.detail?.[0]?.msg || e2.response?.data?.detail || "Could not subscribe. Try again.");
    } finally { setBusy(false); }
  };

  if (result) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 pb-32 text-center" data-testid="culture-club-success">
        <div className="w-14 h-14 mx-auto mb-6 border border-[var(--accent)] flex items-center justify-center">
          <Check size={20} className="text-[var(--accent)]" />
        </div>
        <div className="font-mono-label text-[10px] text-[var(--accent)] mb-3">YOU&apos;RE IN</div>
        <h1 className="font-display text-5xl sm:text-6xl uppercase leading-none mb-5">{result.is_new ? "Welcome to the Culture" : "Already on the list"}</h1>
        <p className="text-neutral-300 text-lg mb-2">
          {result.is_new
            ? <>A welcome email is on its way to <span className="text-[var(--accent)]">{form.email}</span>.</>
            : <>We already had <span className="text-[var(--accent)]">{form.email}</span> — sending you a fresh welcome anyway.</>}
        </p>
        <p className="font-mono-label text-[10px] text-neutral-500 mb-10">CHECK SPAM IF YOU DON&apos;T SEE IT IN 5 MINUTES.</p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/" className="btn-primary" data-testid="back-home-btn">Enter The Culture <ArrowRight size={14} /></Link>
          <Link to="/culture-pass" className="btn-ghost" data-testid="culture-pass-btn">Upgrade to Culture Pass</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32" data-testid="culture-club-page">
      {/* HERO */}
      <section className="relative grain-bg border-b border-neutral-800 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "linear-gradient(180deg, rgba(5,5,5,0.6) 0%, rgba(5,5,5,0.95) 95%), url('https://images.unsplash.com/photo-1571330735066-03aaa9429d89?auto=format&fit=crop&w=1800&q=80')",
            backgroundSize: "cover", backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 py-20 grid lg:grid-cols-[1.1fr_1fr] gap-14 items-start">
          {/* Copy + benefits */}
          <div>
            <div className="font-mono-label text-[11px] text-[var(--accent)] mb-4 rise">CULTURE CLUB · NEWSLETTER</div>
            <h1 className="font-display text-5xl sm:text-7xl md:text-8xl uppercase leading-[0.9] mb-6 rise rise-2">
              Join the <span className="text-[var(--accent)]">Culture</span>
            </h1>
            <p className="text-neutral-300 max-w-xl text-lg mb-10 rise rise-3">
              Exclusive interviews, breaking hip-hop news, giveaways, artist spotlights, event updates — straight to your inbox. No noise. No fluff.
            </p>
            <ul className="space-y-4 mb-8" data-testid="benefits-list">
              {BENEFITS.map((b) => (
                <li key={b.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-neutral-800 flex items-center justify-center shrink-0 mt-0.5">
                    <b.icon size={16} className="text-[var(--accent)]" />
                  </div>
                  <div>
                    <div className="font-headline text-lg uppercase">{b.label}</div>
                    <div className="text-neutral-400 text-sm">{b.body}</div>
                  </div>
                </li>
              ))}
            </ul>
            {subscriberCount > 0 && (
              <p className="font-mono-label text-[10px] text-neutral-500" data-testid="subscriber-count">
                JOIN <span className="text-[var(--accent)]">{subscriberCount.toLocaleString()}</span> MEMBERS IN THE CULTURE CLUB
              </p>
            )}
          </div>

          {/* FORM CARD */}
          <div>
            <div className="card-brut p-7 sm:p-8 border-[var(--accent)] bg-gradient-to-b from-[#1a0a05] to-[#0a0a0a] sticky top-24">
              <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">FREE FOREVER · UNSUBSCRIBE ANYTIME</div>
              <h2 className="font-display text-3xl uppercase mb-2">Get The Drop</h2>
              <p className="text-neutral-400 text-sm mb-6">One or two emails a week, max. Pure culture.</p>

              <form onSubmit={submit} className="space-y-3" data-testid="subscribe-form">
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                  <input
                    required type="text" name="name" placeholder="First Name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="input-brut pl-9" data-testid="subscribe-name"
                  />
                </div>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                  <input
                    required type="email" name="email" placeholder="Email Address"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    className="input-brut pl-9" data-testid="subscribe-email"
                  />
                </div>
                {err && <p className="text-[var(--accent)] font-mono-label text-[10px]" data-testid="subscribe-error">{err}</p>}
                <button type="submit" disabled={busy} className="btn-primary w-full justify-center text-base" data-testid="subscribe-submit">
                  {busy ? "Joining…" : "Join The Culture"}
                </button>
                <p className="font-mono-label text-[9px] text-neutral-500 text-center pt-1">
                  BY JOINING, YOU AGREE TO RECEIVE EMAILS FROM HIPHOPISTHECULTURE. WE NEVER SELL YOUR DATA.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
